/**
 * Shared formatting for evidence-fact strings surfaced in the medical-necessity
 * review UI.
 *
 * Evidence facts embed `DocChunk#NN` references to the clinical-notes chunks the
 * AI relied on. This module parses those references out of the surrounding
 * prose and resolves a reference to the chunk's page/line location (from the
 * item's parallel `chunks_used_locations` list) so a click can deep-link the
 * left-hand PDF.
 * @module evidenceText
 */

/** Matches a `DocChunk#NN` reference plus any trailing `, #NN` chunk numbers. */
const DOC_CHUNK_REFERENCE = /DocChunk#\d+(?:,\s*#\d+)*/g;

/** A single `DocChunk#NN` reference (one number; trailing `#NN` are dropped). */
const DOC_CHUNK_NUMBER = /DocChunk#(\d+)/;

/**
 * One parsed segment of an evidence fact: either a run of plain prose or a
 * `DocChunk#NN` reference. The fact renders as its segments in order.
 */
export interface EvidenceSegment {
  text: string;
  chunkNumber?: number;
}

/** A resolved chunk location used to deep-link the clinical-notes PDF. */
export interface ChunkLocation {
  number: number;
  location: string;
  page?: number;
  line?: number;
}

/**
 * Splits an evidence fact into prose and `DocChunk#NN` segments, in order.
 *
 * A trailing `, #NN` after a reference is treated as part of that reference
 * (it names the same chunk) and is not emitted as a separate segment. Whitespace
 * is preserved on prose segments so the caller can render the original spacing.
 *
 * @param fact - The raw evidence string
 * @returns Ordered segments; prose-only when the fact has no references
 */
export const parseEvidenceText = (fact: string): EvidenceSegment[] => {
  const segments: EvidenceSegment[] = [];
  let cursor = 0;
  DOC_CHUNK_REFERENCE.lastIndex = 0;
  let match = DOC_CHUNK_REFERENCE.exec(fact);
  while (match) {
    if (match.index > cursor) {
      segments.push({ text: fact.slice(cursor, match.index) });
    }
    const number = DOC_CHUNK_NUMBER.exec(match[0]);
    if (number) {
      segments.push({
        text: `DocChunk#${number[1]}`,
        chunkNumber: Number(number[1]),
      });
    } else {
      segments.push({ text: match[0] });
    }
    cursor = match.index + match[0].length;
    match = DOC_CHUNK_REFERENCE.exec(fact);
  }
  if (cursor < fact.length) {
    segments.push({ text: fact.slice(cursor) });
  }
  return segments;
};

/**
 * Builds the lookup used to resolve a `DocChunk#N` reference to its location.
 *
 * Pairs each entry of the item's `chunks_used` citations with the same-index
 * entry of `chunks_used_locations`. Each citation is a free-form string such as
 * `"DocChunk#2 — contributed X"`; only the `DocChunk#N` number is extracted.
 * Citations without a resolvable number, or with no matching location string,
 * yield an unlocatable entry (`location: ""`, no page/line).
 *
 * @param chunksUsed - The item's `chunks_used` citation strings
 * @param chunksUsedLocations - The item's parallel `chunks_used_locations`
 * @returns A map keyed by 1-based chunk number
 */
export const buildChunkLocationsMap = (
  chunksUsed: string[],
  chunksUsedLocations?: string[],
): Map<number, ChunkLocation> => {
  const map = new Map<number, ChunkLocation>();
  chunksUsed.forEach((citation, index) => {
    const match = DOC_CHUNK_NUMBER.exec(citation);
    if (!match) return;
    const number = Number(match[1]);
    if (!map.has(number)) {
      map.set(number, {
        number,
        location: chunksUsedLocations?.[index] ?? "",
      });
    }
  });
  return map;
};

/**
 * Extracts the 1-based page (and, where possible, line) from a
 * `chunks_used_locations` entry formatted `p<start>-<end> l<start>-<end>`.
 *
 * The first (start) page and line are returned so a click can jump to where the
 * chunk begins. Tolerant of the page or line half being absent, a single page
 * (e.g. `p3 l5`), or an unparseable/empty string.
 *
 * @param location - The raw location string (may be empty)
 * @returns The start page and line, each undefined when not present
 */
export const parseChunkLocation = (
  location: string,
): { page?: number; line?: number } => {
  if (!location) return {};
  const page = /\bp(\d+)/.exec(location)?.[1];
  const line = /\bl(\d+)/.exec(location)?.[1];
  return {
    page: page ? Number(page) : undefined,
    line: line ? Number(line) : undefined,
  };
};

/**
 * Enriches a chunk-location map with parsed page/line so the UI can deep-link
 * the PDF without re-parsing on every render.
 *
 * @param map - A map from {@link buildChunkLocationsMap}
 * @returns A new map whose entries carry `page` and `line` when resolvable
 */
export const withParsedLocations = (
  map: Map<number, ChunkLocation>,
): Map<number, ChunkLocation> => {
  const result = new Map<number, ChunkLocation>();
  for (const [number, entry] of map) {
    const { page, line } = parseChunkLocation(entry.location);
    result.set(number, { ...entry, page, line });
  }
  return result;
};
